import { useState } from 'react';
import { Button } from '../ui/Button';
import type { Block } from '../../lib/types';

interface BlockListProps {
    blocks: Block[];
    onEdit: (block: Block) => void;
    onDelete: (blockId: string) => void;
    onToggleEnabled: (blockId: string, enabled: boolean) => void;
}

export function BlockList({ blocks, onEdit, onDelete, onToggleEnabled }: BlockListProps) {
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleDelete = async (blockId: string) => {
        if (deletingId) return;

        if (!confirm('Are you sure you want to delete this block?')) {
            return;
        }

        setDeletingId(blockId);
        try {
            await onDelete(blockId);
        } finally {
            setDeletingId(null);
        }
    };

    if (blocks.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                <div className="text-4xl mb-4">📦</div>
                <p className="font-medium">No blocks yet</p>
                <p className="text-sm">Click "Add New Block" to get started</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {blocks.map((block) => (
                <div
                    key={block.id}
                    className={`
            border-2 rounded-xl p-4 transition-all
            ${block.enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50 opacity-60'}
          `}
                >
                    <div className="flex items-start gap-4">
                        {/* Block Icon */}
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-2xl shrink-0">
                            {block.type === 'link' ? '🔗' : '📝'}
                        </div>

                        {/* Block Content */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-bold text-lg truncate">{block.title}</h3>
                                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500 uppercase">
                                    {block.type}
                                </span>
                            </div>

                            {block.type === 'link' && block.url && (
                                <p className="text-gray-500 text-sm truncate">{block.url}</p>
                            )}

                            {block.type === 'post' && block.content && (
                                <p className="text-gray-500 text-sm line-clamp-2">{block.content}</p>
                            )}

                            {block.type === 'post' && block.imageUrl && (
                                <div className="mt-2">
                                    <img
                                        src={block.imageUrl}
                                        alt={block.title}
                                        className="h-20 w-auto rounded-lg object-cover"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                            {/* Toggle Enabled */}
                            <button
                                onClick={() => onToggleEnabled(block.id, !block.enabled)}
                                className={`
                  w-10 h-6 rounded-full relative transition-colors
                  ${block.enabled ? 'bg-green-500' : 'bg-gray-300'}
                `}
                                title={block.enabled ? 'Disable' : 'Enable'}
                            >
                                <span
                                    className={`
                    absolute top-1 w-4 h-4 bg-white rounded-full transition-transform
                    ${block.enabled ? 'left-5' : 'left-1'}
                  `}
                                />
                            </button>

                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                onClick={() => onEdit(block)}
                                className="px-3 py-2"
                            >
                                ✏️
                            </Button>

                            {/* Delete Button */}
                            <Button
                                variant="ghost"
                                onClick={() => handleDelete(block.id)}
                                className="px-3 py-2 text-red-500 hover:bg-red-50"
                                disabled={deletingId === block.id}
                            >
                                {deletingId === block.id ? '...' : '🗑️'}
                            </Button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
